import {
  NearbyOutletsPaginatedResponse,
  OrderFormat,
  OutletSearchListItem,
  PaginatedResponse,
  SystemVisibility,
} from '@ancon/wildcat-types'
import Moment from 'moment'
import isEmpty from 'lodash/isEmpty'

import api from '../../../api'
import {
  outletsFilterOrderFormats,
  defaultPaginatedResponse,
} from '../constants'
import { GeoPosition } from '../../location/types'
import createAppAsyncThunk from '../../../store/createAppAsyncThunk'
import getTenantIds from '../../app/utils/getTenantIds'
import isAppWhiteLabeled from '../../app/utils/isAppWhiteLabeled'
import getTenantOutletAPIFilters from '../../app/utils/getTenantOutletAPIFilters'
import { AOWAllOrderFormats } from '../../app/constants'

import { outletsFiltersSelector } from './outletsSelectors'

/** One page of the nearby outlets, closest first when there is a location */
async function getNearbyOutlets({
  offset,
  limit,
  search,
  latitude,
  longitude,
  sortByDistance,
}: {
  offset: number
  limit: number
  search?: string
  latitude?: number
  longitude?: number
  sortByDistance: boolean
}): Promise<NearbyOutletsPaginatedResponse> {
  const tenantIds = getTenantIds()
  const tenantOutletFilters = getTenantOutletAPIFilters()

  const orderCriteria = ['state:asc']

  if (sortByDistance) {
    orderCriteria.push('distance:asc')
  }
  orderCriteria.push('name:asc')

  const response = await api.core.outlets.get.nearby({
    offset,
    limit,
    radius: tenantIds?.length ? undefined : 20000, // 20 KM
    orderFormats: AOWAllOrderFormats,
    systemVisibility: SystemVisibility.OrderWeb,
    search,
    latitude,
    longitude,
    order: orderCriteria.join(','),
    ...(!isEmpty(tenantOutletFilters) && {
      filter: tenantOutletFilters,
    }),
  })

  return {
    items: response?.data?.items ?? [],
    meta: {
      count: response?.data?.meta?.count ?? 0,
      offset: response?.data?.meta?.offset ?? 0,
    },
  }
}

export const fetchNearbyOutlets = createAppAsyncThunk<
  NearbyOutletsPaginatedResponse | undefined,
  {
    offset: number
    limit: number
    searchTerm?: string
    geoPosition?: GeoPosition
    keepExisting?: boolean
  }
>(
  'outlets/fetchNearbyOutlets',
  async ({ offset, limit, searchTerm, geoPosition }, { getState }) => {
    const outletsFilters = outletsFiltersSelector(getState())
    const isWhiteLabeled = isAppWhiteLabeled()

    try {
      return await getNearbyOutlets({
        offset,
        limit,
        search: searchTerm ?? (outletsFilters?.searchTerm || undefined),
        latitude: outletsFilters?.searchTerm
          ? undefined
          : geoPosition?.latitude || outletsFilters?.location?.latitude,
        longitude: outletsFilters?.searchTerm
          ? undefined
          : geoPosition?.longitude || outletsFilters?.location?.longitude,
        sortByDistance:
          (isWhiteLabeled || !outletsFilters?.searchTerm) &&
          !!(outletsFilters?.location?.latitude || geoPosition?.latitude),
      })
    } catch (error) {
      return undefined
    }
  },
)

/**
 * A page of restaurants to pick from in the new company order panel, closest
 * first when there is a location. Kept out of the outlet list, so loading
 * the panel doesn't change the list on the outlets page beside it.
 */
export const fetchRestaurantOptions = createAppAsyncThunk<
  NearbyOutletsPaginatedResponse,
  {
    offset: number
    limit: number
  }
>('outlets/fetchRestaurantOptions', async ({ offset, limit }, { getState }) => {
  const location = outletsFiltersSelector(getState())?.location
  const isLocationUsed = !!location?.latitude

  return getNearbyOutlets({
    offset,
    limit,
    latitude: isLocationUsed ? location?.latitude : undefined,
    longitude: isLocationUsed ? location?.longitude : undefined,
    sortByDistance: isLocationUsed,
  })
})

export const searchOutlets = createAppAsyncThunk<
  PaginatedResponse<OutletSearchListItem>,
  {
    search?: string
    limit?: number
    orderFormat?: OrderFormat
  }
>(
  'outlets/searchOutlets',
  async ({ search, limit = 4, orderFormat = outletsFilterOrderFormats }) => {
    const tenantOutletFilters = getTenantOutletAPIFilters()

    const dateTimeUtc = Moment().utc()

    const response = await api.core.outlets.get.search({
      search,
      systemVisibility: SystemVisibility.OrderWeb,
      orderFormats: orderFormat,
      date: dateTimeUtc.toISOString(),
      time: dateTimeUtc.format('HH:mm:ss'),
      offset: 0,
      limit,
      order: 'distance:asc,name:asc',
      ...(!isEmpty(tenantOutletFilters) && {
        filter: tenantOutletFilters,
      }),
    })

    return response?.data || defaultPaginatedResponse
  },
)
